/**
 * The app shell: everything that is on screen no matter which screen you are on.
 *
 * It is deliberately not a web page. There is no site header, no logo bar and no footer —
 * the top of the display belongs to the screen, and the bottom belongs to the instrument.
 * The chrome is a compact transport strip over a tab rail that floats above the home
 * indicator, exactly as in the reference photograph: an acid-yellow chip row for banks, a
 * magenta stop square and a magenta play triangle.
 *
 * Two things live here because nothing else can own them:
 *
 * - **The audio-unlock affordance.** WebKit will not start an `AudioContext` without a
 *   gesture, and an instrument whose first tap is silent is broken. Until the context is
 *   running the shell shows a bar that says so; any pad also unlocks on its own touch.
 * - **The banner host.** Failures reach the user here, because a production build has no
 *   console for them to reach instead.
 */

import { h, list, mount, on, onCleanup, schedule } from '../core/dom';
import { computed, effect, signal, type ReadonlySignal } from '../core/signal';
import { createLogger } from '../core/log';
import { capturePointer, icon, iconButton, transportButton } from '../ui/components/controls';
import { clamp } from '../model/types';
import { banner as bannerView, inertBackground, trapFocus } from '../ui/components/sheet';
import { levelMeter } from '../ui/components/meters';
import { layout } from '../platform/viewport';
import { isStandalone } from '../platform/standalone';
import {
  banners,
  canRedo,
  canUndo,
  currentBank,
  dismissBanner,
  engine,
  notify,
  openProjectID,
  project,
  redo,
  redoLabel,
  reportFailure,
  revision,
  sequencer,
  undo,
  undoLabel,
  type AppBanner,
} from './state';
import { ROUTES, TAB_ROUTES, definition, goBack, navigate, route, selectTab, type RouteName } from './router';

const log = createLogger('shell');

// MARK: - Screen registry
//
// `main.ts` registers every screen factory. The shell never imports a screen, which is what
// keeps the dependency arrow pointing one way: screens depend on the shell's helpers, the
// shell depends only on the router.

export interface ScreenContext {
  readonly projectID?: string;
  readonly packID?: string;
  readonly assetID?: string;
  readonly padID?: string;
}

export type ScreenFactory = (context: ScreenContext) => Node;

const screens = new Map<RouteName, ScreenFactory>();

export function registerScreen(name: RouteName, factory: ScreenFactory): void {
  screens.set(name, factory);
}

// MARK: - Shared screen furniture

export interface ScreenHeaderOptions {
  title: string | ReadonlySignal<string>;
  kicker?: string | ReadonlySignal<string>;
  /** Omitted on tab roots, which have nowhere to go back to. */
  showsBack?: boolean;
  actions?: HTMLElement[];
}

/**
 * The one header shape in the app. It carries its own back control because an installed
 * PWA has no browser chrome to borrow one from.
 */
export function screenHeader(options: ScreenHeaderOptions): HTMLElement {
  const title = typeof options.title === 'string' ? options.title : options.title;
  const kicker = options.kicker;
  return h(
    'header',
    { class: 'screen-header' },
    options.showsBack === false
      ? null
      : h(
          'button',
          {
            class: 'screen-back',
            type: 'button',
            'aria-label': 'Back',
            onclick: () => goBack(),
          },
          icon('chevron.left', { size: 17 }),
        ),
    h(
      'div',
      { class: 'screen-header-titles' },
      kicker === undefined ? null : h('p', { class: 'kicker' }, kicker),
      h('h1', { class: 'screen-title truncate' }, title),
    ),
    h('div', { class: 'screen-header-actions' }, ...(options.actions ?? [])),
  );
}

/** A scrolling body. Screens that are instruments do not use it. */
export function screenBody(...children: (Node | null)[]): HTMLElement {
  return h('div', { class: 'screen-body scroll-y' }, ...children.filter((child): child is Node => child !== null));
}

export function section(title: string, ...children: (Node | null)[]): HTMLElement {
  return h(
    'section',
    { class: 'panel' },
    // An `h2`, not a `p`. Every screen had exactly one heading — its `h1` — so the rotor's
    // heading navigation found one item per screen and a VoiceOver user had to swipe
    // linearly through everything. The class carries the styling either way.
    h('h2', { class: 'kicker panel-kicker' }, title),
    ...children.filter((child): child is Node => child !== null),
  );
}

/**
 * The house callout: a full hairline border, a faint tinted fill, rounded corners and a
 * kicker. Never a coloured bar down the leading edge — that reads dated.
 */
export function callout(
  kicker: string,
  title: string,
  message: string,
  tone: 'neutral' | 'accent' | 'warning' | 'danger' = 'neutral',
  action?: Node,
): HTMLElement {
  return h(
    'div',
    { class: ['callout', tone === 'neutral' ? '' : `callout--${tone === 'accent' ? 'accent' : tone}`] },
    h('p', { class: 'kicker' }, kicker),
    h('h3', { class: 'callout-title' }, title),
    h('p', { class: 'callout-message' }, message),
    action ?? null,
  );
}

export function formatBytes(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0 KB';
  if (value < 1024) return `${Math.round(value)} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / (1024 * 1024)).toFixed(1)} MB`;
  return `${(value / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  // Drum hits are shorter than a second, and "0:00" tells the user nothing about a clip
  // they are about to put on a pad.
  if (seconds < 10) return `${seconds.toFixed(2)} s`;
  const whole = Math.floor(seconds);
  const minutes = Math.floor(whole / 60);
  return `${minutes}:${String(whole % 60).padStart(2, '0')}`;
}

export function formatPreciseTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00.000';
  const minutes = Math.floor(seconds / 60);
  const rest = seconds - minutes * 60;
  return `${minutes}:${rest.toFixed(3).padStart(6, '0')}`;
}

export function formatRelativeDate(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Unknown';
  const elapsed = Date.now() - then;
  const minutes = Math.round(elapsed / 60000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days} d ago`;
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A text field that behaves: no zoom on focus, no autocorrect on a sound name. */
export function textField(options: {
  value: string;
  placeholder?: string;
  label: string;
  onInput: (value: string) => void;
  onCommit?: (value: string) => void;
}): HTMLInputElement {
  const field = h('input', {
    class: 'text-field',
    type: 'text',
    value: options.value,
    placeholder: options.placeholder ?? '',
    'aria-label': options.label,
    autocapitalize: 'sentences',
    autocorrect: 'off',
    spellcheck: 'false',
    oninput: (event: Event) => options.onInput((event.target as HTMLInputElement).value),
    onchange: (event: Event) => options.onCommit?.((event.target as HTMLInputElement).value),
  }) as HTMLInputElement;
  return field;
}

export function rowButton(options: {
  title: string;
  detail?: string;
  symbol?: string;
  destructive?: boolean;
  onPress: () => void;
}): HTMLButtonElement {
  return h(
    'button',
    {
      class: ['list-row', options.destructive === true ? 'list-row--destructive' : ''],
      type: 'button',
      onclick: options.onPress,
    },
    options.symbol === undefined ? null : icon(options.symbol, { size: 17 }),
    h('span', { class: 'list-row-title' }, options.title),
    options.detail === undefined ? null : h('span', { class: 'list-row-detail' }, options.detail),
  ) as HTMLButtonElement;
}

// MARK: - Menus
//
// A long-press menu is the only place several pad actions live, so it also has to be
// reachable without a long press: `presentMenu` is a real dialog with focusable rows, which
// VoiceOver and a hardware keyboard can both drive.

export interface MenuItem {
  title: string;
  symbol?: string;
  detail?: string;
  destructive?: boolean;
  disabled?: boolean;
  /**
   * Present on a row that holds a number rather than a command.
   *
   * A numeric parameter is not an action, and rows that fired one edit and dismissed the sheet
   * made setting velocity to 70% a run of eight long presses with a wrap waiting at the end.
   * With this the row becomes a control that stays put and is dragged.
   */
  value?: MenuValue;
  /** Absent on a value row, which has nothing to fire. */
  onSelect?: () => void;
}

/** A number a menu row owns, adjusted by dragging the row sideways. */
export interface MenuValue {
  /** Value at the moment the menu was built. */
  initial: number;
  min: number;
  max: number;
  /** Granularity. Values snap to this grid measured up from `min`. */
  step: number;
  /** Renders the number for the row and for VoiceOver. */
  format: (value: number) => string;
  /** Called on every change: each drag frame, each arrow key. */
  onChange: (value: number) => void;
  /** Called once, the first time this row is actually moved. */
  onFirstAdjust?: () => void;
  /** Draws the teaching label on this row. */
  hint?: boolean;
}

/**
 * Modal plumbing shared by the menu and the prompt.
 *
 * Both used to be mounted bare — no focus trap, no inert background, no focus restoration —
 * while every other modal in the app went through `createOverlay`. The consequence on the
 * board was concrete: Tab from the last menu item landed on a pad behind the scrim (every
 * tile is `tabindex="0"`), and after any pad action focus dropped to `<body>`, so a keyboard
 * or VoiceOver user restarted from the top of the page after every single action.
 *
 * `inertBackground` is given the overlay *host* rather than the surface: the host is the
 * sibling of the screen and the chrome, so inerting its siblings is what actually takes the
 * board out of the tree.
 */
function presentModal(surface: HTMLElement, scrim: HTMLElement, onEscape: () => void): () => void {
  const host = document.getElementById('padlab-overlays') ?? document.body;
  const dispose = mount(host, scrim);
  const releaseInert = inertBackground(host === document.body ? scrim : host);
  const releaseFocus = trapFocus(surface);
  // Capture, and stop the event dead. The shell's own Escape handler is a bubble listener on
  // `window` registered at launch, so it would otherwise run *first* and navigate back out of
  // the screen while the dialog was still open.
  const offKeys = on(
    window,
    'keydown',
    (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onEscape();
    },
    { capture: true },
  );
  let closed = false;
  return () => {
    if (closed) return;
    closed = true;
    offKeys();
    // Focus goes home before the node leaves, so the browser never has to guess where to
    // put it and never falls back to `<body>`.
    releaseFocus();
    releaseInert();
    dispose();
  };
}

/**
 * One row of a menu that holds a number.
 *
 * The gesture is a horizontal drag and the whole range spans one row's width, so the ends are
 * always inside a thumb's travel and the feel is identical on every parameter. The value clamps
 * at both ends: the wrap this replaced turned one tap too many into a 90 point drop.
 *
 * A div rather than a button, deliberately. A button would synthesise a click on release and
 * take the app's 44 px minimum with it; the div takes the minimum from CSS and cannot be clicked
 * shut. It carries `role="slider"` and `tabindex`, so the focus trap in `presentModal` finds it
 * through `[tabindex]:not([tabindex="-1"])` and a keyboard drives it with arrows.
 */
function menuValueRow(item: MenuItem, spec: MenuValue): HTMLElement {
  let current = clamp(spec.initial, spec.min, spec.max);
  let adjusted = false;

  const fill = h('span', { class: 'menu-value-fill', 'aria-hidden': 'true' });
  const readout = h('span', { class: 'menu-value-readout numeric' });
  const hint = spec.hint === true ? h('span', { class: 'kicker menu-value-hint' }, 'Drag') : null;

  const row = h(
    'div',
    {
      class: 'menu-item menu-value',
      role: 'slider',
      tabindex: '0',
      'aria-label': item.title,
      'aria-valuemin': String(spec.min),
      'aria-valuemax': String(spec.max),
    },
    fill,
    item.symbol === undefined ? null : icon(item.symbol, { size: 17 }),
    h('span', { class: 'menu-item-title truncate' }, item.title),
    hint,
    icon('arrow.left.and.right', { size: 13, class: 'menu-value-glyph' }),
    readout,
  );

  const render = (): void => {
    readout.textContent = spec.format(current);
    const span = Math.max(spec.max - spec.min, Number.EPSILON);
    fill.style.setProperty('width', `${((current - spec.min) / span) * 100}%`);
    row.setAttribute('aria-valuenow', String(current));
    row.setAttribute('aria-valuetext', spec.format(current));
  };

  /** Snaps to the step grid measured from the lower bound, then clamps. It never wraps. */
  const setValue = (next: number): void => {
    const snapped = spec.min + Math.round((next - spec.min) / spec.step) * spec.step;
    const clamped = clamp(snapped, spec.min, spec.max);
    if (Math.abs(clamped - current) < spec.step * 1e-3) return;
    current = clamped;
    render();
    spec.onChange(current);
  };

  const markAdjusted = (): void => {
    if (adjusted) return;
    adjusted = true;
    hint?.remove();
    spec.onFirstAdjust?.();
  };

  let pointerId: number | null = null;
  let originX = 0;
  let originValue = current;
  let moved = false;

  onCleanup(
    row,
    on(row, 'pointerdown', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointerId !== null) return;
      pointerId = pointer.pointerId;
      originX = pointer.clientX;
      originValue = current;
      moved = false;
      capturePointer(row, pointer.pointerId);
      row.classList.add('is-dragging');
    }),
  );

  onCleanup(
    row,
    on(row, 'pointermove', (event: Event) => {
      const pointer = event as PointerEvent;
      if (pointer.pointerId !== pointerId) return;
      const dx = pointer.clientX - originX;
      // Two pixels of slop, so a tap that trembles is still a tap and changes nothing.
      if (!moved && Math.abs(dx) < 2) return;
      moved = true;
      // Measured live: the row is the track, so the sweep is honest on any screen width.
      const travel = Math.max(row.getBoundingClientRect().width, 160);
      setValue(originValue + (dx / travel) * (spec.max - spec.min));
    }),
  );

  const endDrag = (event: Event): void => {
    const pointer = event as PointerEvent;
    if (pointer.pointerId !== pointerId) return;
    pointerId = null;
    row.classList.remove('is-dragging');
    if (moved) markAdjusted();
  };
  onCleanup(row, on(row, 'pointerup', endDrag));
  onCleanup(row, on(row, 'pointercancel', endDrag));

  onCleanup(
    row,
    on(row, 'keydown', (event: Event) => {
      const key = (event as KeyboardEvent).key;
      const coarse = spec.step * 10;
      if (key === 'ArrowRight' || key === 'ArrowUp') setValue(current + spec.step);
      else if (key === 'ArrowLeft' || key === 'ArrowDown') setValue(current - spec.step);
      else if (key === 'PageUp') setValue(current + coarse);
      else if (key === 'PageDown') setValue(current - coarse);
      else if (key === 'Home') setValue(spec.min);
      else if (key === 'End') setValue(spec.max);
      else return;
      event.preventDefault();
      markAdjusted();
    }),
  );

  render();
  return row;
}

/**
 * A menu card, with one optional line of prose under the title.
 *
 * The title is a `kicker`: 10px, bold, uppercase, letter-spaced. That is a label, and a
 * sentence set as a label is not read as a sentence. "Quantise, heard only while playing"
 * shipped in that slot and taught nobody anything. `note` is where the sentence goes, in
 * caption ink under the label, for the controls whose behaviour cannot be guessed from their
 * options. Measured on a 393px screen: 58 characters is one line, 52 at the largest type
 * scale. Longer wraps, which a paragraph may do and a row's detail may not.
 */
export function presentMenu(title: string, items: readonly MenuItem[], note?: string): void {
  let close = (): void => {};
  const card = h(
    'div',
    { class: 'menu-card safe-bottom', role: 'dialog', 'aria-modal': 'true', 'aria-label': title },
    h('p', { class: 'kicker menu-title' }, title),
    note === undefined ? null : h('p', { class: 'menu-note' }, note),
    h(
      'div',
      { class: 'menu-items' },
      ...items.map((item) => {
        // A value row is never dismissed by touching it, which is the whole point of it.
        if (item.value !== undefined) return menuValueRow(item, item.value);
        return h(
          'button',
          {
            class: ['menu-item', item.destructive === true ? 'menu-item--destructive' : ''],
            type: 'button',
            disabled: item.disabled === true,
            onclick: () => {
              close();
              item.onSelect?.();
            },
          },
          item.symbol === undefined ? null : icon(item.symbol, { size: 17 }),
          h('span', { class: 'menu-item-title' }, item.title),
          item.detail === undefined ? null : h('span', { class: 'menu-item-detail' }, item.detail),
        );
      }),
    ),
    h('button', { class: 'menu-cancel', type: 'button', onclick: () => close() }, 'Cancel'),
  );
  const scrim = h(
    'div',
    {
      class: 'menu-scrim',
      role: 'presentation',
      onclick: (event: Event) => {
        if (event.target === event.currentTarget) close();
      },
    },
    card,
  );
  close = presentModal(card, scrim, () => close());
}

/** Modal text prompt — rename flows need one and the platform's own is not styleable. */
export function presentPrompt(options: {
  title: string;
  message?: string;
  value: string;
  confirmTitle?: string;
  onConfirm: (value: string) => void;
}): void {
  let close = (): void => {};
  const field = textField({ value: options.value, label: options.title, onInput: () => {} });
  const card = h(
    'form',
    {
      class: 'prompt-card',
      role: 'dialog',
      'aria-modal': 'true',
      'aria-label': options.title,
      onsubmit: (event: Event) => {
        event.preventDefault();
        const value = field.value.trim();
        close();
        if (value !== '') options.onConfirm(value);
      },
    },
    h('h2', { class: 'prompt-title' }, options.title),
    options.message === undefined ? null : h('p', { class: 'prompt-message' }, options.message),
    field,
    h(
      'div',
      { class: 'prompt-actions' },
      h('button', { class: 'prompt-cancel', type: 'button', onclick: () => close() }, 'Cancel'),
      h('button', { class: 'prompt-confirm', type: 'submit' }, options.confirmTitle ?? 'Save'),
    ),
  );
  const scrim = h(
    'div',
    {
      class: 'menu-scrim',
      role: 'presentation',
      onclick: (event: Event) => {
        if (event.target === event.currentTarget) close();
      },
    },
    card,
  );
  close = presentModal(card, scrim, () => close());
  // `trapFocus` has already put focus in the field (it is the first focusable in the form);
  // selecting the text is what makes a rename one keystroke rather than a cursor hunt.
  field.select();
}

// MARK: - History

/**
 * Undo, said out loud.
 *
 * `undo()` restores a snapshot and changes nothing a screen reader would notice, so the label
 * of the step that was reversed goes through the banner host — which is already the app's one
 * polite live region. Without it a keyboard user hears silence and cannot tell whether the
 * shortcut landed.
 */
function runUndo(): void {
  const label = undoLabel.peek();
  if (label === null) return;
  undo();
  notify('Undone', label);
}

function runRedo(): void {
  const label = redoLabel.peek();
  if (label === null) return;
  redo();
  notify('Redone', label);
}

/**
 * The undo/redo pair, for screens that edit the document.
 *
 * It is a pair of controls rather than a menu item because the label has to be readable
 * before the press: "Undo Clear Pad" is the difference between confidence and a second
 * mistake.
 */
export function historyButtons(): readonly HTMLElement[] {
  return [
    iconButton({
      symbol: 'arrow.counterclockwise',
      label: computed(() => {
        const step = undoLabel.value;
        return step === null ? 'Undo, nothing to undo' : `Undo ${step}`;
      }),
      enabled: canUndo,
      onPress: () => runUndo(),
    }).el,
    iconButton({
      symbol: 'arrow.clockwise',
      label: computed(() => {
        const step = redoLabel.value;
        return step === null ? 'Redo, nothing to redo' : `Redo ${step}`;
      }),
      enabled: canRedo,
      onPress: () => runRedo(),
    }).el,
  ];
}

// MARK: - Transport strip

/**
 * The bank chips.
 *
 * Plain buttons in a group, not a `tablist` of `tab`s. The tab pattern brings roving
 * tabindex with it — every unselected chip gets `tabindex="-1"` — which is only navigable if
 * the container implements arrow keys, and this one never did: Tab skipped the other banks
 * and nothing else could reach them, so a keyboard user could not leave bank A. The app's
 * own tab rail was moved off the same pattern for the same reason.
 */
function transportStrip(): HTMLElement {
  const playing = computed(() => engine.transport.playing.value);
  const tempo = computed(() => {
    revision.value;
    return project.value?.tempo ?? 96;
  });
  const position = computed(() => engine.transport.position.value);

  const meter = levelMeter({ orientation: 'horizontal', segments: 14, title: 'Master output', class: 'transport-meter' });
  const release = engine.acquireMeters();
  const stop = effect(() => {
    engine.meterTick.value;
    const level = engine.masterLevel();
    meter.setLevel(level.rms, level.peak);
  });

  const strip = h(
    'div',
    { class: 'transport', role: 'group', 'aria-label': 'Transport' },
    // No bank rail here. The board already carries a bank control at the top, with the name
    // spelled out and room for it, so this was the same thing twice on one screen: change one
    // and the other moved, which reads as two controls arguing. On the sequencer it was worse
    // than redundant, because tracks bind to pads directly and switching bank there changed
    // nothing at all. The width it used goes to the meter, which was being squeezed to its
    // 40px minimum on the one screen where watching the output actually matters.
    h(
      'div',
      { class: 'transport-readout' },
      h('span', { class: 'kicker' }, 'Tempo'),
      h('span', { class: 'numeric transport-tempo' }, computed(() => `${Math.round(tempo.value)}`)),
      // The second number needs its own name. Stacked bare under a "Tempo" kicker it read as
      // part of the tempo: the owner asked what "fine tempo" did, and there is no such
      // control. It is bar and beat, and during a count-in it showed a bare "-3", which is
      // worse than unlabelled.
      h(
        'span',
        { class: 'transport-position' },
        h('span', { class: 'kicker' }, computed(() => (position.value.isCountingIn ? 'Count in' : 'Bar'))),
        h('span', { class: 'numeric' }, computed(() => {
          const at = position.value;
          return at.isCountingIn ? `${at.countInBeatsRemaining}` : `${at.bar}.${at.beat}`;
        })),
      ),
    ),
    meter.el,
    h(
      'div',
      { class: 'transport-buttons' },
      // Panic is not repeated here. It did exactly what the board's own Panic button does, so
      // the transport carried a duplicate of a destructive control sitting one thumb-width
      // from Play, wearing a bare exclamation mark that reads as a warning rather than a
      // button. The board keeps it, labelled with the actual word.
      transportButton({ kind: 'stop', enabled: playing, onPress: () => sequencer.stop() }).el,
      transportButton({ kind: 'play', active: playing, onPress: () => sequencer.toggle() }).el,
    ),
  );
  onCleanup(strip, () => {
    stop();
    release();
    meter.destroy();
  });
  return strip;
}

// MARK: - Tab rail

/**
 * These are navigation controls, not tabs over panels: pressing one replaces the screen and
 * changes the URL. `role="tab"` requires an owning `tablist`, and claiming both the
 * `navigation` landmark and the tab pattern on one subtree left the selection state
 * unreliable. Plain buttons with `aria-current="page"` say the true thing and need no
 * `tablist`/`tabpanel` plumbing.
 */
function tabRail(): HTMLElement {
  const rail = h('nav', { class: 'tab-rail', 'aria-label': 'Sections' });
  for (const entry of TAB_ROUTES) {
    const tab = entry.tab!;
    const active = computed(() => route.value.name === entry.name);
    const button = h(
      'button',
      {
        class: 'tab',
        type: 'button',
        'aria-label': tab.title,
        onclick: () => selectTab(entry.name, project.peek()?.id),
      },
      icon(tab.symbol, { size: 20 }),
      h('span', { class: 'tab-title' }, tab.title),
    );
    const stop = effect(() => {
      const on = active.value;
      button.classList.toggle('tab--active', on);
      if (on) button.setAttribute('aria-current', 'page');
      else button.removeAttribute('aria-current');
    });
    onCleanup(button, stop);
    rail.appendChild(button);
  }
  return rail;
}

// MARK: - Banner host

function bannerHost(): HTMLElement {
  const host = h('div', { class: 'banner-host', role: 'status', 'aria-live': 'polite' });
  list(
    host,
    banners,
    (entry: AppBanner) => entry.id,
    (entry: AppBanner) => {
      const view = bannerView({
        style: entry.kind === 'danger' ? 'error' : entry.kind === 'success' ? 'success' : entry.kind === 'warning' ? 'warning' : 'info',
        title: entry.title,
        message: entry.message,
        ...(entry.actionLabel === undefined ? {} : { actionTitle: entry.actionLabel }),
        ...(entry.action === undefined
          ? {}
          : {
              onAction: () => {
                dismissBanner(entry.id);
                entry.action?.();
              },
            }),
        onDismiss: () => dismissBanner(entry.id),
      });
      return view.el;
    },
  );
  return host;
}

// MARK: - Audio unlock

/**
 * WebKit refuses to start an `AudioContext` outside a gesture. Rather than let the first
 * pad tap be silent, the shell says so and offers a control whose only job is to be tapped.
 */
function unlockBar(): HTMLElement {
  const needsUnlock = computed(() => !engine.isUnlocked.value);
  const bar = h(
    'button',
    {
      class: 'unlock-bar',
      type: 'button',
      onclick: () => {
        void engine.unlock();
      },
    },
    icon('speaker.slash.fill', { size: 17 }),
    h('span', null, 'Tap to turn on sound'),
    h('span', { class: 'kicker' }, 'iOS starts audio on a touch'),
  );
  const stop = effect(() => {
    const hidden = !needsUnlock.value;
    bar.classList.toggle('unlock-bar--hidden', hidden);
    bar.setAttribute('aria-hidden', hidden ? 'true' : 'false');
    bar.tabIndex = hidden ? -1 : 0;
  });
  onCleanup(bar, stop);
  return bar;
}

// MARK: - Shell

const missingScreen = (name: string): HTMLElement =>
  h(
    'div',
    { class: 'screen' },
    screenHeader({ title: 'Not available', showsBack: true }),
    screenBody(callout('Navigation', 'This screen is not registered', `PadLab tried to open "${name}". Go back and try again.`, 'warning')),
  );

/** Builds the shell and mounts it into `root`. Returns a disposer. */
export function createShell(root: HTMLElement): () => void {
  const screenHost = h('main', { class: 'screen-host', id: 'padlab-screen' });
  // No `safe-bottom` here. Padding the container cannot be absorbed by the rail inside it:
  // the rail keeps its full height and the inset lands as an inert band under the icons.
  // The rail owns the inset instead, in screens.css.
  const chrome = h('div', { class: 'chrome' });
  const overlays = h('div', { class: 'overlay-host', id: 'padlab-overlays' });

  const transport = transportStrip();
  const tabs = tabRail();
  chrome.append(transport, tabs);

  // Without this a keyboard user tabs the whole tab rail and toolbar again on every screen
  // change before reaching the content — on the board, before reaching a single pad.
  const skipLink = h(
    'a',
    {
      class: 'skip-link',
      href: '#padlab-screen',
      onclick: (event: Event) => {
        event.preventDefault();
        screenHost.focus();
        screenHost.scrollIntoView();
      },
    },
    'Skip to the screen',
  );
  screenHost.tabIndex = -1;

  const shell = h(
    'div',
    { class: 'shell' },
    skipLink,
    unlockBar(),
    bannerHost(),
    screenHost,
    chrome,
    overlays,
  );

  // One screen at a time. The old tree is disposed, not hidden: a live pad grid left in the
  // background would keep metering and keep pointer handlers alive.
  let disposeScreen: (() => void) | null = null;
  const stopRoute = effect(() => {
    const current = route.value;
    // A deep link is painted before `bootstrap()` has opened the document, so the screen has
    // to be rebuilt once it arrives — otherwise the link lands on "no project open".
    openProjectID.value;
    const factory = screens.get(current.name);
    const context: ScreenContext = {
      ...(current.projectID === undefined ? {} : { projectID: current.projectID }),
      ...(current.packID === undefined ? {} : { packID: current.packID }),
      ...(current.assetID === undefined ? {} : { assetID: current.assetID }),
      ...(current.padID === undefined ? {} : { padID: current.padID }),
    };
    const node = factory === undefined ? missingScreen(current.name) : factory(context);
    disposeScreen?.();
    screenHost.replaceChildren();
    disposeScreen = mount(screenHost, node);
    screenHost.scrollTop = 0;
  });

  const stopChrome = effect(() => {
    const meta = definition.value;
    const open = project.value !== null;
    chrome.classList.toggle('chrome--no-transport', !meta.showsTransport || !open);
    chrome.classList.toggle('chrome--no-tabs', !meta.showsTabs);
    transport.hidden = !meta.showsTransport || !open;
    tabs.hidden = !meta.showsTabs;
    document.documentElement.dataset['route'] = meta.name;
  });

  const stopLayout = effect(() => {
    const metrics = layout.value;
    shell.classList.toggle('shell--landscape', metrics.orientation === 'landscape');
    shell.classList.toggle('shell--compact', metrics.widthClass === 'compact');
  });

  // An attribute rather than a `(display-mode: standalone)` media query: iOS Safari reports a
  // Home Screen launch only through `navigator.standalone`, which CSS cannot see at all, and
  // the desktop device frame must not appear inside an installed window.
  const stopStandalone = effect(() => {
    document.documentElement.dataset['standalone'] = isStandalone.value ? 'true' : 'false';
  });

  /**
   * The last stop for a promise nobody handled.
   *
   * `log.ts` compiles out of a production build, so a rejection left to the console is a
   * failure with no witness: the user sees an action quietly do nothing and has nothing to
   * report. Routing it into the banner host makes it visible and dismissible, and
   * `preventDefault` keeps the duplicate console report from being the story in development.
   */
  const offRejections = on(window, 'unhandledrejection', (event: PromiseRejectionEvent) => {
    event.preventDefault();
    log.error('Unhandled rejection', event.reason);
    reportFailure('PadLab could not finish that', event.reason);
  });

  // Keyboard shortcuts for hardware keyboards and iPad. Space is the transport, which is
  // what every other music app on the platform does.
  const offKeys = on(window, 'keydown', (event: KeyboardEvent) => {
    const target = event.target as HTMLElement | null;
    if (target !== null && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) return;
    // Space on a focused button fires keydown *then* click, so preventing the default here
    // would swallow the activation. Inside a dialog that meant Space on a menu row started
    // the transport instead of choosing the row — on the only route to the pad actions.
    if (target !== null && target.closest('[role="dialog"]') !== null) return;
    if (event.key === ' ' && definition.peek().showsTransport) {
      event.preventDefault();
      sequencer.toggle();
      return;
    }
    // ⌘Z / ⌃Z, and both of the redo spellings the two platforms use. The stack was complete
    // and unreachable: nothing in the UI called `undo()`, while four dialogs named it as the
    // way back. `notify` speaks the label, so the result is announced rather than silent.
    if ((event.metaKey || event.ctrlKey) && (event.key === 'z' || event.key === 'Z')) {
      event.preventDefault();
      if (event.shiftKey) runRedo();
      else runUndo();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && (event.key === 'y' || event.key === 'Y')) {
      event.preventDefault();
      runRedo();
      return;
    }
    if (event.key === 'Escape' && route.peek().name !== 'home') goBack();
  });

  const disposeShell = mount(root, shell);

  // The first committed frame is what removes the boot screen. Doing it on a scheduled
  // callback rather than immediately means the shell is really painted first.
  schedule(() => {
    document.documentElement.dataset['appReady'] = 'true';
    document.getElementById('padlab-boot')?.remove();
  });

  return () => {
    offKeys();
    offRejections();
    stopRoute();
    stopChrome();
    stopLayout();
    stopStandalone();
    disposeScreen?.();
    disposeShell();
  };
}

/** Used by screens that need a route-aware "open this project" jump. */
export function openProjectRoute(name: RouteName, projectID: string | undefined): void {
  void navigate({ name, ...(projectID === undefined ? {} : { projectID }) });
}

export const shellRouteTitle = computed(() => ROUTES[route.value.name].title);

/** True while any bank exists to swipe between — the board's swipe hint reads this. */
export const hasMultipleBanks = computed(() => {
  revision.value;
  return (project.value?.banks.length ?? 0) > 1;
});

export const currentBankName = computed(() => currentBank.value?.name ?? 'Bank');

/** A shared "no project open" state, used by every screen that needs a document. */
export function requiresProject(): HTMLElement {
  return h(
    'div',
    { class: 'screen' },
    screenHeader({ title: 'No project open', showsBack: true }),
    screenBody(
      callout(
        'Nothing loaded',
        'Open a project first',
        'Pick a recent project or start from a template on the Home screen.',
        'accent',
        h(
          'div',
          { class: 'callout-actions' },
          iconButton({ symbol: 'square.grid.3x3', label: 'Go to Home', onPress: () => selectTab('home') }).el,
        ),
      ),
    ),
  );
}

export const overlayHost = (): HTMLElement => document.getElementById('padlab-overlays') ?? document.body;

export const signalOf = signal;
